import { ActivatedRoute, Router } from '@angular/router';
import {
    AnimalAddressForm,
    AnimalForm,
    ChipCodeForm,
    ChipInformationForm,
    ChipRegistrationRootForm,
    NewChipPayload,
    OwnerForm,
    Signaturs,
    UploadPdfResponse,
} from '@app/models/chip.model';
import { ChipDetailsResponse, OwnerDetailResponse } from '@app/shared/models/chip-details.model';
import { Component, DestroyRef, OnInit, effect, inject } from '@angular/core';
import { EMPTY, catchError, concatMap, debounceTime, distinctUntilChanged, filter, finalize, from, map, of, switchMap, take } from 'rxjs';
import {
    FormBuilder,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';

import { AnimalAddressFormComponent } from '@app/shared/chip-forms/animal-address-form/animal-address-form.component';
import { AnimalFormComponent } from '@app/shared/chip-forms/animal-form/animal-form.component';
import { Button } from "primeng/button";
import { ChipInfoFormComponent } from '@app/shared/chip-forms/chip-info-form/chip-info-form.component';
import { ChipSearchComponent } from '@app/pages/chip-registration/components/chip-search/chip-search.component';
import { ChipService } from '@app/pages/chip-registration/services/chip.service';
import { DataService } from '@user/services/data.service';
import { LoadingService } from '@app/services/loading.service';
import { OwnerFormComponent } from '@app/shared/chip-forms/owner-form/owner-form.component';
import { SignatureCaptureComponent } from '@shared/components/signature/signature.component';
import { ToastService } from '@app/services/toast.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Veterinarian } from '@app/pages/clinic/models/clinic.model';
import { buildChipForm } from '@app/shared/helpers/build-chip-form.helper';
import { generateChangeOwnerPdf } from '@app/shared/helpers/chip-change-owner-pdf-generator.helper';
import { isCode15 } from '@app/models/chip-code.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toCamelCase } from '@shared/helpers/camel-case-converter.helper';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'app-change-owner',
    imports: [
    ChipSearchComponent,
    ReactiveFormsModule,
    ChipInfoFormComponent,
    TranslatePipe,
    OwnerFormComponent,
    AnimalFormComponent,
    AnimalAddressFormComponent,
    Button,
    SignatureCaptureComponent
],
    templateUrl: './change-owner.component.html',
    styleUrl: './change-owner.component.scss',
    providers: [FormGroupDirective, ChipService],
})
export class ChangeOwnerComponent implements OnInit {
    private fb = inject(FormBuilder);
    private dataService = inject(DataService);
    private chipService = inject(ChipService);
    private destroyRef = inject(DestroyRef);
    readonly toastService = inject(ToastService);
    readonly loadingService = inject(LoadingService);
    readonly router = inject(Router);
    private route = inject(ActivatedRoute);

    veterinariansList: Veterinarian[] = [];
    rootFormGroup: FormGroup<ChipRegistrationRootForm>;
    animalAddressSameAsOwner = false;
    edInStock: number | undefined = 0;
    chipDetails!: ChipDetailsResponse;
    isSignatureDialogVisible = false;
    searchedOwnerResponse: OwnerDetailResponse | null = null;

    constructor() {
        this.rootFormGroup = buildChipForm(this.fb, 'ChangeOwner');

        effect(() => {
            const vets = this.dataService.veterinarian();
            if (vets) {
                this.veterinariansList = [...vets];
            }

            this.edInStock = this.dataService.clinic()?.ed_modification_in_stock;
        });

        this.rootFormGroup
            .get('animal')?.get('isAddressSameAsOwner')
                ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((value) => {
                    this.animalAddressSameAsOwner = value;
                    this.updateValidationForAnimalAddress(value);
                });

        this.rootFormGroup
            .get('owner')
            ?.get('isAddressSameAsOwner')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (value !== undefined) {
                    this.rootFormGroup
                        .get('animal')
                        ?.get('isAddressSameAsOwner')?.setValue(value);
                }
            });
    }

    ngOnInit(): void {
        const routeCode = this.route.parent?.snapshot.paramMap.get('code');
        if (routeCode && isCode15(routeCode)) {
            this.chipService
                .getChipDetailInfo(routeCode)
                .subscribe((res) => {
                    this.chipDetails = res;
                    this.updateFormValues();
                    this.updateChipModificationStatus(!!this.chipDetails.codeInfo.code);
                    this.rootFormGroup.updateValueAndValidity();
                });
        }

        this.rootFormGroup.get('owner')?.get('idNumber')?.valueChanges
            .pipe(
                filter(value => !!value),
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((value) =>
                    {
                        const { owner } = this.rootFormGroup.getRawValue();
                        return this.dataService.searchOwnerByPassportInfo(owner.idType, owner.idNumber).pipe(
                            catchError(() => EMPTY)
                        )

                    }
                )
            )
            .subscribe((ownerResponse: OwnerDetailResponse | null) => {
                this.updateOwnerAfterSearch(ownerResponse);
            });
    }

    onSubmit(): void {
        const {
            chip,
            chipInformation: chipInformation,
            owner,
            animal,
            animalAddress,
            vetSignature,
            ownerSignature,
            previousOwnerSignature
        } = this.rootFormGroup.getRawValue();
        const {
            idType,
            idNumber,
            nameSurname,
            email,
            phone,
            ...ownerAdress
        } = owner;
        if (chip.code !== null && this.rootFormGroup.valid && ownerSignature && vetSignature) {
            const payloadForNewChip: NewChipPayload = {
                chip: {
                    id: this.chipDetails.codeInfo.id,
                    code: chip.code,
                },
                chipInformation: {
                    id: this.chipDetails.registrationDetails.id,
                    veterinaryId: chipInformation.vetId,
                    chipReaded: chipInformation.chipReaded,
                    date: chipInformation.date,
                },
                owner: {
                    idType: owner.idType.toString(),
                    idNumber: owner.idNumber,
                    fullName: owner.nameSurname,
                    email: owner.email,
                    phone: owner.phone,
                },
                animal: { id: this.chipDetails.animal.id, ...animal },
                address: ownerAdress,
                animalAddress: animalAddress,
                ownerSignature,
                vetSignature,
                previousOwnerSignature
            };
            if (this.searchedOwnerResponse) {
                payloadForNewChip.owner.id = this.searchedOwnerResponse.id;
                payloadForNewChip.address.id = this.searchedOwnerResponse.addresses.id;
            }
            this.loadingService.show();
            this.chipService
                .chipChangeOwner(payloadForNewChip)
                .pipe(
                    concatMap((chipRegistration) => {
                        this.toastService.success('Form saved successfully');
                        const mappedChipDetailsResponse = this.chipService.mapChipDetailsResponse(chipRegistration);
                        return from(
                            generateChangeOwnerPdf(mappedChipDetailsResponse, this.chipDetails.owner),
                        ).pipe(
                            map((pdfBlob) => ({ pdfBlob, mappedChipDetailsResponse })), // combine response + pdf
                        );
                    }),
                    concatMap(({ pdfBlob, mappedChipDetailsResponse }) => {
                        return this.chipService.uploadPdfChangeOwner(
                            pdfBlob,
                            this.chipDetails.owner.email,
                            mappedChipDetailsResponse
                        );
                    }),
                    catchError((err) => {
                        this.toastService.error(err?.message);
                        return of(err);
                    }),
                    finalize(() => this.loadingService.hide()),
                )
                .subscribe((pdfUploadResponse: UploadPdfResponse) => {
                    this.toastService.success('PDF uploaded and email sent');
                    this.router.navigate(['/dashboard', 'registered-chips', chip.code]).then();
                });
        }
    }

    async openPdf(): Promise<void> {
        await generateChangeOwnerPdf(toCamelCase(this.chipDetails), this.chipDetails.owner);
    }

    updateChipModificationStatus(isChipFound: boolean): void {
        if (isChipFound) {
            this.rootFormGroup.get('chip')?.disable();
            this.rootFormGroup.get('animal')?.disable();
            this.rootFormGroup.get('chipInformation')?.disable();
            this.rootFormGroup.get('animal')?.get('isAddressSameAsOwner')?.enable();
            this.rootFormGroup.get('chipInformation')?.get('vetId')?.enable();
        }
    }

    updateValidationForAnimalAddress(isAddressSameAsOwner: boolean): void {
        const animalAddressForm = this.getAnimalAddressForm();
        const ownerAddressForm = this.getOwnerForm();
        for (const key in animalAddressForm.controls) {
            if (key) {
                const formControl = animalAddressForm.get(key);
            if (formControl) {
                if (isAddressSameAsOwner) {
                    formControl.setValue(ownerAddressForm.get(key)?.value);
                    formControl.disable();
                } else {
                    formControl.enable();
                }
                formControl.setValidators(isAddressSameAsOwner ? [] : [Validators.required]);
                formControl.updateValueAndValidity();
            }
            }
        }
    }

    handleSignatureSave(signaturs: Signaturs): void {
        if (signaturs.veterinarian && signaturs.owner && signaturs.previousOwner) {
            this.rootFormGroup.get('vetSignature')?.setValue(signaturs.veterinarian);
            this.rootFormGroup.get('ownerSignature')?.setValue(signaturs.owner);
            this.rootFormGroup.get('previousOwnerSignature')?.setValue(signaturs.previousOwner);
            this.isSignatureDialogVisible = false;
        }
    }

    handleOwnerSearch(): void {
        const { owner } = this.rootFormGroup.getRawValue();
        this.dataService.searchOwnerByPassportInfo(owner.idType, owner.idNumber)
            .pipe(take(1))
            .subscribe((ownerResponse: OwnerDetailResponse | null) => {
                this.updateOwnerAfterSearch(ownerResponse);
            });
    }

    updateOwnerAfterSearch(ownerResponse: OwnerDetailResponse | null): void {
        if (ownerResponse) {
            this.searchedOwnerResponse = ownerResponse;
            this.rootFormGroup.patchValue({
                owner: {
                    idType: +ownerResponse.idType,
                    idNumber: ownerResponse.idNumber,
                    nameSurname: ownerResponse.fullName,
                    countryId: +ownerResponse.addresses.countryId,
                    regionId: +ownerResponse.addresses.regionId,
                    settlementId: +ownerResponse.addresses.settlementId,
                    address: ownerResponse.addresses.address,
                    zipCode: ownerResponse.addresses.zipCode,
                    phone: ownerResponse.phone,
                    email: ownerResponse.email,
                }
            })
        }
        this.rootFormGroup.updateValueAndValidity();
    }

    getChipForm(): FormGroup<ChipCodeForm> {
        return this.rootFormGroup.controls['chip'] as FormGroup<ChipCodeForm>;
    }

    getChipInfoForm(): FormGroup<ChipInformationForm> {
        return this.rootFormGroup.controls['chipInformation'] as FormGroup<ChipInformationForm>;
    }

    getOwnerForm(): FormGroup<OwnerForm> {
        return this.rootFormGroup.controls['owner'] as FormGroup<OwnerForm>;
    }

    getAnimalForm(): FormGroup<AnimalForm> {
        return this.rootFormGroup.controls['animal'] as FormGroup<AnimalForm>;
    }

    getAnimalAddressForm(): FormGroup<AnimalAddressForm> {
        return this.rootFormGroup.controls['animalAddress'] as FormGroup<AnimalAddressForm>;
    }

    updateFormValues(): void {
        this.rootFormGroup.patchValue({
            chip: {
                code: this.chipDetails.codeInfo.code,
            },
            chipInformation: {
                vetId: this.chipDetails.veterinarian.id,
                selectedVetDocumentId: this.chipDetails.veterinarian.veterinaryId,
                date: new Date(),
                chipReaded: true,
            },
            owner: {
                idType: +this.chipDetails.owner.idType,
                idNumber: this.chipDetails.owner.idNumber,
                nameSurname: this.chipDetails.owner.fullName,
                countryId: +this.chipDetails.owner.addresses.countryId,
                regionId: +this.chipDetails.owner.addresses.regionId,
                settlementId: +this.chipDetails.owner.addresses.settlementId,
                address: this.chipDetails.owner.addresses.address,
                zipCode: this.chipDetails.owner.addresses.zipCode,
                phone: this.chipDetails.owner.phone,
                email: this.chipDetails.owner.email,
            },
            animal: {
                    type: this.chipDetails.animal.type,
                    breed: +this.chipDetails.animal.breed,
                    name: this.chipDetails.animal.name,
                    sex: +this.chipDetails.animal.sex,
                    dateOfBirth: this.chipDetails.animal.dateOfBirth,
                    chipLocation: +this.chipDetails.animal.chipLocation,
                    coatType: +this.chipDetails.animal.coatType,
                    size: +this.chipDetails.animal.size,
                    castrated: this.chipDetails.animal.castrated,
                    isAddressSameAsOwner: false,
            },
            animalAddress: {
                countryId: +this.chipDetails.animal.addresses.countryId,
                regionId: +this.chipDetails.animal.addresses.regionId,
                settlementId: +this.chipDetails.animal.addresses.settlementId,
                address: this.chipDetails.animal.addresses.address,
                zipCode: this.chipDetails.animal.addresses.zipCode,
            },
            ownerSignature: null,
            vetSignature: null,
            previousOwnerSignature: null,
        }, { emitEvent: true, onlySelf: false });
    }
}
